import { useMemo, useState } from 'react';
import type { ZodError } from 'zod';

import { getGlobalAPIHeaders } from '@data/global-api-common';
import { useGlobalApiQuery } from '@data/global-api-common';
import {
  captureException,
  CURRENT_SERVICE_TYPE,
} from '@utility/checkout-utils';
import type { UseQueryResult } from '@utility/checkout-utils';

import { GLOBAL_API_PAYMENTS_ENDPOINTS } from '../../constants/endpoints';
import { GlobalApiPaymentsDetailsSchema } from '../../schemas/payment';
import type { GlobalApiPaymentDetails } from '../../types/payment';

import type { UseGetGlobalApiPaymentsDetailsParams } from './useGetGlobalApiPaymentsDetails.types';

export function useGetGlobalApiPaymentsDetails({
  authToken,
  paymentId,
  skip,
  voltApp,
}: UseGetGlobalApiPaymentsDetailsParams): UseQueryResult<GlobalApiPaymentDetails> {
  const [validationError, setValidationError] = useState<ZodError | null>(null);
  const { result, error, isError, ...rest } =
    useGlobalApiQuery<GlobalApiPaymentDetails>({
      url: GLOBAL_API_PAYMENTS_ENDPOINTS.getPaymentDetails(
        paymentId,
        CURRENT_SERVICE_TYPE
      ),
      headers: getGlobalAPIHeaders({ authToken, voltApp, voltService: 'pis' }),
      skip,
    });

  const paymentDetails = useMemo(() => {
    if (result === null) {
      return result;
    }

    const validationResult = GlobalApiPaymentsDetailsSchema.safeParse(result);

    if (!validationResult.success) {
      // eslint-disable-next-line react-hooks/set-state-in-render
      setValidationError(validationResult.error);
      captureException(validationResult.error);
    }

    return result;
  }, [result]);

  const isZodError = Boolean(validationError);

  return {
    result: paymentDetails,
    error: validationError ?? error,
    isError: isZodError || isError,
    ...rest,
  };
}
